/**
 * Utilidades de validación para tarjetas de crédito/débito
 */

export type CardBrand = "visa" | "mastercard" | "amex" | "discover" | "unknown";

/**
 * Detecta la marca de la tarjeta según el número (BIN)
 */
export function detectCardBrand(cardNumber: string): CardBrand {
  const cleaned = cardNumber.replaceAll(/\s/g, "");
  if (!cleaned) return "unknown";

  // Visa: starts with 4
  if (cleaned.startsWith("4")) return "visa";

  // Mastercard: 51-55 or 2221-2720
  if (/^5[1-5]/.test(cleaned)) return "mastercard";
  const fourDigit = Number.parseInt(cleaned.slice(0, 4), 10);
  if (cleaned.length >= 4 && fourDigit >= 2221 && fourDigit <= 2720) return "mastercard";

  // AMEX: 34 or 37
  if (/^3[47]/.test(cleaned)) return "amex";

  // Discover: 6011, 622126-622925, 644-649, 65
  if (cleaned.startsWith("6011") || cleaned.startsWith("65")) return "discover";
  if (/^64[4-9]/.test(cleaned)) return "discover";
  if (cleaned.length >= 6) {
    const sixDigit = Number.parseInt(cleaned.slice(0, 6), 10);
    if (sixDigit >= 622126 && sixDigit <= 622925) return "discover";
  }

  return "unknown";
}

/**
 * Algoritmo de Luhn para validar número de tarjeta
 */
export function validateLuhn(cardNumber: string): boolean {
  const cleaned = cardNumber.replaceAll(/\s/g, "");
  if (!/^\d+$/.test(cleaned)) return false;

  let sum = 0;
  let isEven = false;

  for (let i = cleaned.length - 1; i >= 0; i--) {
    let digit = Number.parseInt(cleaned[i], 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
}

/**
 * Valida la fecha de expiración
 */
export function validateExpiry(month: string, year: string): boolean {
  const m = Number.parseInt(month, 10);
  const y = Number.parseInt(year, 10);

  if (m < 1 || m > 12) return false;

  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;

  if (y < currentYear) return false;
  if (y === currentYear && m < currentMonth) return false;

  return true;
}

/**
 * Valida el CVV según la marca
 */
export function validateCVV(cvv: string, brand: CardBrand): boolean {
  const cleaned = cvv.replaceAll(/\s/g, "");
  if (!/^\d+$/.test(cleaned)) return false;

  if (brand === "amex") return cleaned.length === 4;
  return cleaned.length === 3;
}

/**
 * Formatea el número de tarjeta con espacios
 */
export function formatCardNumber(value: string): string {
  const cleaned = value.replaceAll(/\D/g, "");
  const brand = detectCardBrand(cleaned);

  if (brand === "amex") {
    // AMEX: 4-6-5
    const parts = [cleaned.slice(0, 4), cleaned.slice(4, 10), cleaned.slice(10, 15)];
    return parts.filter(Boolean).join(" ");
  }

  // Resto: 4-4-4-4
  const parts = cleaned.match(/.{1,4}/g);
  return parts ? parts.join(" ") : cleaned;
}

/**
 * Obtiene la longitud máxima del número según la marca
 */
export function getMaxCardLength(brand: CardBrand): number {
  if (brand === "amex") return 15;
  return 16;
}
